import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { newId, RECURRENCE_FREQS, type Freq } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { useRpc } from '../../data/people.ts'
import { paramSummary, say, settingLines, typeLabel, useDocumentVersions, useTaskTypes, useTemplates, useTemplateVersions, type ParamDef, type TemplateVersionRow } from '../../data/plan.ts'
import {
  blankRule, buildRecurrencePublish, currentTemplateVersion, daysInMonth, defaultInterval, describeRule, draftOfRule, draftRuleVersion, fmtDay, fmtDayLong, langOf, MAX_EXDATES, MAX_ROTATION,
  monthName, moveItem, paramValuesOf, previewDates, republishWithTemplateVersion, ruleProblems, rulesInForce, templateRows, weekdayName, type RecRow, type RecVersion, type RuleDraft, type TemplateView, type TFn,
} from '../../data/planSetup.ts'
import { localDate } from '../../lib/time.ts'
import { Banner, errorText, Field, Pill } from '../../ui/components.tsx'
import { useWorkshopAction } from '../../ui/hooks.ts'
import { LookStrip } from '../../ui/looks/LookStrip.tsx'
import { ParamField } from '../../ui/ParamField.tsx'
import { CycleProposal } from '../plan/CycleProposal.tsx'
import { CheckRow, ConfirmAction, digits, Feedback, Problems, ToggleChip, useSetupAccess } from './shared.tsx'

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/
type Access = ReturnType<typeof useSetupAccess>
type Action = ReturnType<typeof useWorkshopAction>
type Reload = () => Promise<void>

/** Recurrences: the rules that make jobs by themselves. A rule is a list of immutable versions; the database generates the jobs, this screen writes the rules. */
export function RecurrencesTab() {
  const { t, i18n } = useTranslation()
  const lang = langOf(i18n.language)
  const { org, site } = useClub()
  const tts = useTaskTypes(); const templates = useTemplates(); const tvs = useTemplateVersions(); const docVersions = useDocumentVersions()
  const access = useSetupAccess('recurrence.publish')
  const rpc = useRpc<RecRow[]>('plan_recurrences', { p_org: org.org_id, p_site: site.site_id }, access.permitted)
  const w = useWorkshopAction()
  const [editing, setEditing] = useState<{ from: { rec: RecRow; newest: RecVersion } | null } | null>(null)
  const today = localDate(new Date(), site.timezone)
  const tplRows = useMemo(() => templateRows(templates ?? [], tvs ?? [], tts ?? [], docVersions ?? [], lang), [templates, tvs, tts, docVersions, lang])
  const rows = useMemo(() => {
    const title = (r: RecRow) => { const x = tplRows.find((y) => y.tpl.id === r.job_template_id); return x ? say(x.latest.title, lang) : '' }
    return [...(rpc.data ?? [])].sort((a, b) => Number(!!a.archived_at) - Number(!!b.archived_at) || title(a).localeCompare(title(b), lang))
  }, [rpc.data, tplRows, lang])
  const tf = t as TFn
  if (!tts || !templates || !tvs || !docVersions) return <p>{t('common.loading')}</p>

  const after = async (ok: Promise<boolean>): Promise<boolean> => { const r = await ok; if (r) void rpc.reload(); return r }
  if (editing) {
    return <RuleForm key={editing.from?.rec.recurrence_id ?? 'new'} from={editing.from} tplRows={tplRows} tvs={tvs} today={today} w={w} access={access} reload={rpc.reload}
      onClose={() => setEditing(null)} />
  }
  return (
    <section className="flex flex-col gap-4" data-testid="rec-list-view">
      <div className="flex flex-col items-start gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-bold">{t('planSetup.rec.title')}</h2>
          <p className="text-sm opacity-75" data-testid="rec-help">{t('planSetup.rec.help')}</p>
        </div>
        {access.permitted && <button className="btn btn-primary !min-h-11" disabled={!access.canSave} onClick={() => { w.reset(); setEditing({ from: null }) }} data-testid="rec-new">{t('planSetup.rec.new')}</button>}
      </div>
      {!access.permitted && <Banner tone="info">{t('planSetup.rec.denied')}</Banner>}
      <Feedback error={w.error} ok={w.ok} pending={w.pending} />
      {access.permitted && rpc.error && <Banner tone="error"><span data-testid="rec-load-error">{errorText(t, rpc.error)}</span></Banner>}
      {access.permitted && !rpc.data && !rpc.error && <p className="opacity-70">{rpc.online ? t('common.loading') : t('planSetup.rec.needsNetworkToRead')}</p>}
      {access.permitted && rpc.data && rows.length === 0 && <p className="opacity-70" data-testid="rec-none">{t('planSetup.rec.none')}</p>}
      <ul className="flex flex-col gap-3" data-testid="rec-list">
        {rows.map((r) => {
          const { newest, deciding } = rulesInForce(r.versions ?? [], today)
          if (!newest) return null
          const tpl = tplRows.find((x) => x.tpl.id === r.job_template_id)
          const used = tvs.find((x) => x.id === newest.template_version_id)
          const current = currentTemplateVersion(r.job_template_id, tvs)
          const outdated = !r.archived_at && !!used && !!current && current.status === 'published' && used.id !== current.id
          return (
            <li key={r.recurrence_id} className="card flex flex-col gap-2" data-testid="rec-row" data-recurrence-id={r.recurrence_id}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="text-lg font-bold" data-testid="rec-row-title">{tpl ? say(tpl.latest.title, lang) : '…'}</p>
                <div className="flex flex-wrap gap-1.5">
                  {r.archived_at && <Pill tone="gray">{t('planSetup.archived')}</Pill>}
                  <Pill tone="gray">{t('planSetup.rec.versions', { count: (r.versions ?? []).length })}</Pill>
                </div>
              </div>
              {tpl && <p className="text-sm opacity-80">{typeLabel(tpl.taskType, tpl.tpl.task_type_code, lang)}{used ? ` · ${t('planSetup.rec.templateVersion', { n: used.version_no })}` : ''}</p>}
              <p className="font-medium" data-testid="rec-rule">{describeRule(newest, tf, lang)}</p>
              {deciding && deciding.version_no !== newest.version_no && <p className="text-sm opacity-80" data-testid="rec-until-then">{t('planSetup.rec.untilThen', { rule: describeRule(deciding, tf, lang) })}</p>}
              <p className="text-sm opacity-80" data-testid="rec-live-jobs">{t('planSetup.rec.liveJobs', { count: r.live_jobs })}</p>
              {outdated && current && (
                <div className="flex flex-col gap-2 rounded-lg border-2 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900" data-testid="rec-outdated">
                  <p>{t('planSetup.rec.outdated', { n: used?.version_no ?? 0, latest: current.version_no })}</p>
                  {access.permitted && (
                    <button className="btn !min-h-11 self-start" disabled={!access.canSave || w.busy} data-testid="rec-use-latest"
                      onClick={() => void after(w.run('recurrence.publish@1', republishWithTemplateVersion({ recurrenceId: r.recurrence_id, versionId: newId() }, newest, current.id), 'planSetup.rec.updatedTemplate', { aggregateId: r.recurrence_id }))}>
                      {t('planSetup.rec.useLatest')}
                    </button>
                  )}
                </div>
              )}
              {access.permitted && !r.archived_at && (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <button className="btn !min-h-11" disabled={!access.canSave} onClick={() => { w.reset(); setEditing({ from: { rec: r, newest } }) }} data-testid="rec-edit">{t('planSetup.newVersion')}</button>
                  <ConfirmAction id="rec-archive" label={t('planSetup.rec.archive')} message={t('planSetup.rec.archiveAsk')} confirmLabel={t('planSetup.rec.archiveYes')} busy={w.busy} disabled={!access.canSave} danger
                    onConfirm={() => after(w.run('recurrence.archive@1', { recurrence_id: r.recurrence_id }, 'planSetup.rec.archivedDone', { aggregateId: r.recurrence_id }))} />
                </div>
              )}
              {(r.versions ?? []).length > 1 && (
                <details>
                  <summary className="min-h-11 cursor-pointer py-2 text-sm font-semibold">{t('planSetup.rec.history')}</summary>
                  <ul className="flex flex-col gap-1 text-sm" data-testid="rec-history">
                    {[...(r.versions ?? [])].sort((a, b) => b.version_no - a.version_no).map((v) => <li key={v.version_id}>{t('planSetup.rec.versionLine', { n: v.version_no, rule: describeRule(v, tf, lang) })}</li>)}
                  </ul>
                </details>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** A 'MM-DD' as two selects: the day list follows the month, so an impossible date cannot be picked. */
function MonthDayPick({ value, onChange, lang, id }: { value: string; onChange: (md: string) => void; lang: string; id: string }) {
  const { t } = useTranslation()
  const m = Number(value.slice(0, 2)) || 1, dd = Number(value.slice(3, 5)) || 1
  const set = (mo: number, da: number) => onChange(`${String(mo).padStart(2, '0')}-${String(Math.min(da, daysInMonth(mo))).padStart(2, '0')}`)
  return (
    <div className="grid grid-cols-[1fr_5.5rem] gap-2">
      <select className="input" aria-label={t('planSetup.rec.month')} value={m} onChange={(e) => set(Number(e.target.value), dd)} data-testid={`${id}-month`}>
        {Array.from({ length: 12 }, (_, i) => <option key={i} value={i + 1}>{monthName(i + 1, lang)}</option>)}
      </select>
      <select className="input" aria-label={t('planSetup.rec.day')} value={dd} onChange={(e) => set(m, Number(e.target.value))} data-testid={`${id}-day`}>
        {Array.from({ length: daysInMonth(m) }, (_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
      </select>
    </div>
  )
}

/** `from` null = a new recurrence; otherwise the next version of that rule, prefilled from the newest. The template is fixed once the rule exists. */
function RuleForm({ from, tplRows, tvs, today, w, access, reload, onClose }: {
  from: { rec: RecRow; newest: RecVersion } | null; tplRows: TemplateView[]; tvs: TemplateVersionRow[]; today: string; w: Action; access: Access; reload: Reload; onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const lang = langOf(i18n.language)
  const ids = useRef({ recurrenceId: from?.rec.recurrence_id ?? newId(), versionId: newId() })
  const [tplId, setTplId] = useState(from?.rec.job_template_id ?? '')
  const [d, setD] = useState<RuleDraft>(() => (from ? draftOfRule(from.newest) : blankRule(today)))
  const [exdate, setExdate] = useState('')
  const [openStep, setOpenStep] = useState<number | null>(null)               // one rotation step's fields open at a time (M25-S: a phone keeps a list of rows)
  const tpl = tplRows.find((x) => x.tpl.id === tplId)
  const current = tpl ? currentTemplateVersion(tpl.tpl.id, tvs) : undefined
  const tt = tpl?.taskType
  const defs: ParamDef[] = useMemo(() => tt?.param_defs ?? [], [tt])
  const saved = useMemo(() => from?.rec.versions ?? [], [from])
  const nextNo = Math.max(0, ...saved.map((v) => v.version_no)) + 1
  const problems = [...(current ? [] : ['TEMPLATE_MISSING']), ...ruleProblems(d, defs)]
  const draftVersion = useMemo(() => draftRuleVersion(d, nextNo, defs), [d, nextNo, defs])
  // the next dates from today, or from the day the new rule begins when that is later (a rule that starts in a year would otherwise show none)
  const previewFrom = DAY_RE.test(d.from) && d.from > today ? d.from : today
  const dates = useMemo(() => (draftVersion ? previewDates(saved, draftVersion, previewFrom) : []), [draftVersion, saved, previewFrom])
  const rotationOf = (versionNo: number): Array<Record<string, string | number>> | null =>
    versionNo === nextNo ? (d.rotation ? d.rotation.map((s) => paramValuesOf(defs, s)) : null) : (saved.find((v) => v.version_no === versionNo)?.rotation ?? null)
  const linesOf = (step: Record<string, string | number> | undefined) => (step ? settingLines(step, tt, lang) : [])
  const stepLines = (versionNo: number, index: number) => linesOf(rotationOf(versionNo)?.[index])
  const stepText = (versionNo: number, index: number): string => { const step = rotationOf(versionNo)?.[index]; return step ? paramSummary(step, tt, lang) : '' }
  const moving = from && current && from.newest.template_version_id !== current.id ? tvs.find((x) => x.id === from.newest.template_version_id) : undefined

  const patch = (p: Partial<RuleDraft>) => setD((x) => ({ ...x, ...p }))
  const changeFreq = (f: Freq) => patch({ freq: f, interval: defaultInterval(f) })
  const toggleDay = (n: number) => patch({ weekdays: d.weekdays.includes(n) ? d.weekdays.filter((x) => x !== n) : [...d.weekdays, n].sort((a, b) => a - b) })
  const addExdate = () => { if (/^\d{4}-\d{2}-\d{2}$/.test(exdate) && !d.exdates.includes(exdate) && d.exdates.length < MAX_EXDATES) patch({ exdates: [...d.exdates, exdate].sort() }); setExdate('') }
  const setStep = (i: number, key: string, value: string) => setD((x) => ({ ...x, rotation: (x.rotation ?? []).map((s, j) => (j === i ? { ...s, [key]: value } : s)) }))
  const moveStep = (i: number, by: -1 | 1) => { patch({ rotation: moveItem(d.rotation ?? [], i, by) }); setOpenStep(openStep === i ? i + by : openStep === i + by ? i : openStep) }
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (problems.length > 0 || !current || !tpl) return
    const payload = buildRecurrencePublish({ ...ids.current, ...(from ? {} : { jobTemplateId: tpl.tpl.id }) }, current.id, d, defs)
    if (await w.run('recurrence.publish@1', payload, 'planSetup.rec.published', { aggregateId: ids.current.recurrenceId })) { void reload(); onClose() }
  }
  const intervalFor = d.freq === 'daily' ? null : d.freq

  return (
    <form noValidate onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" data-testid="rec-form">
      <button type="button" className="self-start text-sm font-semibold underline" onClick={onClose} data-testid="rec-cancel">← {t('common.back')}</button>
      <h2 className="text-xl font-bold">{from ? t('planSetup.rec.editTitle') : t('planSetup.rec.newTitle')}</h2>
      <p className="text-sm opacity-75">{t('planSetup.rec.help')}</p>

      {from === null ? (
        <Field label={t('planSetup.rec.template')} hint={t('planSetup.rec.templateHelp')} htmlFor="rec-template">
          <select id="rec-template" className="input" value={tplId} onChange={(e) => setTplId(e.target.value)} data-testid="rec-template">
            <option value="">{t('planSetup.rec.templateChoose')}</option>
            {tplRows.map((r) => <option key={r.tpl.id} value={r.tpl.id}>{say(r.latest.title, lang)} — {typeLabel(r.taskType, r.tpl.task_type_code, lang)}</option>)}
          </select>
        </Field>
      ) : (
        <div className="flex flex-col gap-1"><p className="text-sm font-semibold">{t('planSetup.rec.template')}</p><p data-testid="rec-template-fixed">{tpl ? say(tpl.latest.title, lang) : '…'}</p></div>
      )}
      {current && <p className="meta" data-testid="rec-template-version">{t('planSetup.rec.templateUsed', { n: current.version_no })}{moving ? ` ${t('planSetup.rec.templateMoves', { old: moving.version_no })}` : ''}</p>}

      <Field label={t('planSetup.rec.freq')} htmlFor="rec-freq">
        <select id="rec-freq" className="input" value={d.freq} onChange={(e) => changeFreq(e.target.value as Freq)} data-testid="rec-freq">
          {RECURRENCE_FREQS.map((f) => <option key={f} value={f}>{t(`planSetup.freq.${f}`)}</option>)}
        </select>
      </Field>

      {d.freq === 'weekly' && (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold">{t('planSetup.rec.weekdays')}</legend>
          <div className="grid grid-cols-7 gap-1" data-testid="rec-weekdays">
            {[1, 2, 3, 4, 5, 6, 7].map((n) => <ToggleChip key={n} className="!min-w-0 !px-0" on={d.weekdays.includes(n)} label={weekdayName(n, lang)} testId={`rec-weekday-${n}`} onClick={() => toggleDay(n)}>{weekdayName(n, lang, 'short')}</ToggleChip>)}
          </div>
        </fieldset>
      )}
      {d.freq === 'monthly' && (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold">{t('planSetup.rec.monthdays')}</legend>
          <input id="rec-monthdays" className="input" autoComplete="off" placeholder="1, 15" aria-label={t('planSetup.rec.monthdays')} value={d.monthdays} onChange={(e) => patch({ monthdays: e.target.value })} data-testid="rec-monthdays" />
          <p className="meta">{t('planSetup.rec.monthdaysHelp')}</p>
          <div><ToggleChip on={d.monthLast} testId="rec-monthday-last" onClick={() => patch({ monthLast: !d.monthLast })}>{t('planSetup.rec.monthLast')}</ToggleChip></div>
        </fieldset>
      )}
      {intervalFor && (
        <Field label={t(`planSetup.rec.interval.${intervalFor}`)} hint={t(`planSetup.rec.intervalHelp.${intervalFor}`)} htmlFor="rec-interval">
          <input id="rec-interval" className="input" inputMode="numeric" value={d.interval} onChange={(e) => patch({ interval: digits(e.target.value) })} data-testid="rec-interval" />
        </Field>
      )}

      <Field label={t('planSetup.rec.start')} hint={t('planSetup.rec.startHelp')} htmlFor="rec-start"><input id="rec-start" type="time" className="input" value={d.start} onChange={(e) => patch({ start: e.target.value })} data-testid="rec-start" /></Field>

      <fieldset className="flex flex-col gap-2" data-testid="rec-season">
        <CheckRow checked={d.season} onChange={(on) => patch({ season: on })} testId="rec-season-on">{t('planSetup.rec.season')}</CheckRow>
        {d.season && (
          <>
            <p className="meta">{t('planSetup.rec.seasonHelp')}</p>
            <div className="flex flex-col gap-1"><p className="text-sm font-semibold">{t('planSetup.rec.seasonStart')}</p><MonthDayPick id="rec-season-start" lang={lang} value={d.seasonStart} onChange={(v) => patch({ seasonStart: v })} /></div>
            <div className="flex flex-col gap-1"><p className="text-sm font-semibold">{t('planSetup.rec.seasonEnd')}</p><MonthDayPick id="rec-season-end" lang={lang} value={d.seasonEnd} onChange={(v) => patch({ seasonEnd: v })} /></div>
          </>
        )}
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('planSetup.rec.from')} hint={t(from ? 'planSetup.rec.fromHelpEdit' : 'planSetup.rec.fromHelpNew')} htmlFor="rec-from"><input id="rec-from" type="date" className="input" value={d.from} onChange={(e) => patch({ from: e.target.value })} data-testid="rec-from" /></Field>
        <Field label={t('planSetup.rec.to')} htmlFor="rec-to"><input id="rec-to" type="date" className="input" value={d.to} min={d.from || undefined} onChange={(e) => patch({ to: e.target.value })} data-testid="rec-to" /></Field>
      </div>

      <fieldset className="flex flex-col gap-2" data-testid="rec-exdates">
        <legend className="mb-1 text-sm font-semibold">{t('planSetup.rec.exdates')}</legend>
        <p className="meta">{t('planSetup.rec.exdatesHelp')}</p>
        <div className="flex flex-wrap gap-2">
          {d.exdates.map((x) => (
            <span key={x} className="inline-flex min-h-11 items-center gap-1 rounded-xl border-2 border-slate-300 pl-3 text-sm dark:border-slate-600" data-testid="rec-exdate-chip" data-date={x}>
              {fmtDay(x)}<button type="button" className="grid min-h-11 min-w-11 place-items-center text-lg" aria-label={`${t('common.remove')} ${fmtDay(x)}`} onClick={() => patch({ exdates: d.exdates.filter((y) => y !== x) })} data-testid={`rec-exdate-remove-${x}`}>×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="date" className="input" aria-label={t('planSetup.rec.exdates')} value={exdate} onChange={(e) => setExdate(e.target.value)} data-testid="rec-exdate" />
          <button type="button" className="btn" disabled={!exdate || d.exdates.length >= MAX_EXDATES} onClick={addExdate} data-testid="rec-exdate-add">{t('planSetup.rec.exdateAdd')}</button>
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-2" data-testid="rec-rotation">
        <CheckRow checked={d.rotation !== null} disabled={defs.length === 0} testId="rec-rotation-on" onChange={(on) => { patch({ rotation: on ? [{}, {}] : null }); setOpenStep(null) }}>{t('planSetup.rec.rotation')}</CheckRow>
        <p className="meta">{defs.length === 0 ? t('planSetup.rec.rotationNoSettings') : t('planSetup.rec.rotationHelp')}</p>
        {/* M25-A's panel: the whole cycle proposed by the written rule (only where the viewer holds job.plan and worklog.read), filling the steps below */}
        <CycleProposal tt={tt} areaIds={current?.default_area_ids ?? []} start={d.from} onFill={(steps) => { patch({ rotation: steps }); setOpenStep(null) }} />
        {d.rotation && (
          <ol className="flex flex-col gap-2" data-testid="rec-rotation-list">
            {d.rotation.map((step, i) => {
              const lines = linesOf(paramValuesOf(defs, step))
              const open = openStep === i
              const btn = 'btn !min-h-14 !px-3'
              return (
                <li key={i} className="card flex flex-col gap-3" data-testid="rec-rotation-step" data-step={i + 1}>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <h4 className="font-bold">{t('planSetup.rec.rotationStep', { n: i + 1 })}</h4>
                    <LookStrip lines={lines} className="text-brand dark:text-fairway" />
                    <span data-testid="rec-rotation-summary">{lines.length > 0 ? paramSummary(paramValuesOf(defs, step), tt, lang) : t('planSetup.rec.rotationStepEmpty')}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className={btn} disabled={i === 0} onClick={() => moveStep(i, -1)} aria-label={`${t('planSetup.moveUp')} — ${t('planSetup.rec.rotationStep', { n: i + 1 })}`} data-testid={`rec-rotation-up-${i}`}>{t('planSetup.moveUp')}</button>
                    <button type="button" className={btn} disabled={i === (d.rotation?.length ?? 0) - 1} onClick={() => moveStep(i, 1)} aria-label={`${t('planSetup.moveDown')} — ${t('planSetup.rec.rotationStep', { n: i + 1 })}`} data-testid={`rec-rotation-down-${i}`}>{t('planSetup.moveDown')}</button>
                    <button type="button" className={btn} aria-expanded={open} aria-label={t('planSetup.rec.rotationStepOpen', { n: i + 1 })} onClick={() => setOpenStep(open ? null : i)} data-testid={`rec-rotation-edit-${i}`}>{open ? t('planSetup.rec.rotationClose') : t('common.edit')}</button>
                  </div>
                  {open && (
                    <div className="flex flex-col gap-3" data-testid={`rec-rotation-fields-${i}`}>
                      {defs.map((def) => <ParamField key={def.key} def={def} tt={tt} value={step[def.key] ?? ''} onChange={(v) => setStep(i, def.key, v)} idPrefix={`rec-rotation-${i}`} lang={lang} />)}
                      <button type="button" className={`${btn} self-start`} disabled={(d.rotation?.length ?? 0) <= 1} onClick={() => { patch({ rotation: (d.rotation ?? []).filter((_, j) => j !== i) }); setOpenStep(null) }} data-testid={`rec-rotation-remove-${i}`}>{t('common.remove')}</button>
                    </div>
                  )}
                </li>
              )
            })}
          </ol>
        )}
        {d.rotation && <button type="button" className="btn" disabled={d.rotation.length >= MAX_ROTATION} onClick={() => { patch({ rotation: [...(d.rotation ?? []), {}] }); setOpenStep(d.rotation?.length ?? 0) }} data-testid="rec-rotation-add">{t('planSetup.rec.rotationAdd')}</button>}
      </fieldset>

      <section className="flex flex-col gap-2" data-testid="rec-preview" aria-live="polite">
        <h3 className="text-lg font-bold">{t('planSetup.rec.preview')}</h3>
        {!draftVersion ? <p className="text-sm opacity-70" data-testid="rec-preview-empty">{t('planSetup.rec.previewEmpty')}</p> : dates.length === 0 ? <p className="text-sm opacity-70" data-testid="rec-preview-none">{t('planSetup.rec.previewNone')}</p> : (
          <>
            <ol className="flex flex-col gap-1">
              {dates.map((o) => (
                <li key={o.date} className="text-sm" data-testid="rec-preview-date" data-date={o.date}>
                  <span className="font-semibold">{fmtDayLong(o.date, lang)}</span>{stepText(o.version_no, o.rotation_index) && <> · <LookStrip lines={stepLines(o.version_no, o.rotation_index)} className="text-brand dark:text-fairway" /> <span className="opacity-80">{stepText(o.version_no, o.rotation_index)}</span></>}
                </li>
              ))}
            </ol>
            <p className="meta">{t('planSetup.rec.previewMore')}</p>
          </>
        )}
      </section>

      <Problems codes={problems} />
      <Feedback error={w.error} pending={w.pending} />
      <button className="btn btn-primary" disabled={w.busy || !access.canSave || problems.length > 0} data-testid="rec-save">{w.busy ? t('common.saving') : t('planSetup.rec.publish')}</button>
      {!access.online && <p className="meta">{t('planSetup.needsNetwork')}</p>}
    </form>
  )
}
